import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import QMSLayout from "../components/QMSLayout";
import { API } from "../App";
import {
  Shield, Play, Square, Clock, Send, Copy, MessageSquare,
  ChevronDown, ChevronRight, Plus, Loader2, CheckCircle2,
  AlertTriangle, X, FileText, Search, BookOpen, Zap,
  User, ArrowRight, Phone, Hash, Radio
} from "lucide-react";

const STANDARDS_LIST = [
  "ISO 13485:2016", "FDA QMSR (21 CFR 820)", "21 CFR Part 11", "ALCOA+ Principles",
  "AS9100D", "IATF 16949", "ISO 17025", "ISO 14971",
  "FDA PV 2011", "ISO 22514", "ISO 9001:2015", "GAMP 5"
];

const QUICK_QUERY_KEYS = [
  { labelKey: "warRoom.quickQueries.explainResult", icon: Search, prefix: "Explain this result: " },
  { labelKey: "warRoom.quickQueries.justifyDecision", icon: Shield, prefix: "Justify this decision: " },
  { labelKey: "warRoom.quickQueries.standardRef", icon: BookOpen, prefix: "What standard covers: " },
  { labelKey: "warRoom.quickQueries.defenseScript", icon: Zap, prefix: "Generate defense script for: " },
];

const CONFIDENCE_STYLES = {
  HIGH: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  MEDIUM: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  LOW: "bg-red-500/20 text-red-400 border-red-500/30",
};

const STATUS_STYLES = {
  new: { bg: "bg-blue-500/20", text: "text-blue-400", label: "New" },
  assigned: { bg: "bg-purple-500/20", text: "text-purple-400", label: "Assigned" },
  sme_ready: { bg: "bg-emerald-500/20", text: "text-emerald-400", label: "SME Ready" },
  escalation: { bg: "bg-amber-500/20", text: "text-amber-400", label: "Escalation" },
  critical: { bg: "bg-red-500/20", text: "text-red-400", label: "Critical" },
  resolved: { bg: "bg-[#132340]", text: "text-[#94A3B8]", label: "Resolved" },
};

const AuditWarRoomPage = () => {
  const { t } = useTranslation('audit');
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [queryText, setQueryText] = useState("");
  const [queryContext, setQueryContext] = useState({ standard: "ISO 13485:2016", clause: "", report_id: "", metric: "", value: "" });
  const [queryLog, setQueryLog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [querying, setQuerying] = useState(false);
  const [showNewSession, setShowNewSession] = useState(false);
  const [newSession, setNewSession] = useState({ standard: "ISO 13485:2016", clause: "", report_id: "", auditor_name: "", auditor_org: "", finding_risk: "MEDIUM" });
  const [elapsedTime, setElapsedTime] = useState(0);
  const [copiedId, setCopiedId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [relayingId, setRelayingId] = useState(null);
  const [assignSme, setAssignSme] = useState("");
  const timerRef = useRef(null);
  const queryInputRef = useRef(null);

  const fetchSessions = useCallback(async () => {
    try {
      const res = await fetch(`${API}/auditiq/sessions?limit=20`, { credentials: "include" });
      if (res.ok) { const d = await res.json(); setSessions(d.sessions || []); }
    } catch (e) { console.error(e); }
  }, []);

  const fetchQueryLog = useCallback(async (sessionId) => {
    try {
      const url = statusFilter
        ? `${API}/auditiq/query-log?session_id=${sessionId}&status=${statusFilter}&limit=50`
        : `${API}/auditiq/query-log?session_id=${sessionId}&limit=50`;
      const res = await fetch(url, { credentials: "include" });
      if (res.ok) { const d = await res.json(); setQueryLog(d.logs || []); }
    } catch (e) { console.error(e); }
  }, [statusFilter]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  useEffect(() => {
    if (activeSession?.status === "active") {
      const startTime = new Date(activeSession.started_at).getTime();
      timerRef.current = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
      return () => clearInterval(timerRef.current);
    } else { clearInterval(timerRef.current); setElapsedTime(0); }
  }, [activeSession]);

  useEffect(() => {
    if (activeSession) fetchQueryLog(activeSession.session_id);
  }, [activeSession, fetchQueryLog]);

  const formatTime = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${sec.toString().padStart(2, "0")}`;
  };

  const startSession = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/auditiq/sessions`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...newSession, loaded_standards: STANDARDS_LIST.slice(0, 4) }),
      });
      if (res.ok) { const session = await res.json(); setActiveSession(session); setShowNewSession(false); fetchSessions(); }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const endSession = async () => {
    if (!activeSession) return;
    try {
      await fetch(`${API}/auditiq/sessions/${activeSession.session_id}/end`, { method: "PUT", credentials: "include" });
      setActiveSession(null); fetchSessions();
    } catch (e) { console.error(e); }
  };

  const submitQuery = async () => {
    if (!queryText.trim() || querying) return;
    setQuerying(true);
    try {
      const payload = {
        session_id: activeSession?.session_id || "",
        query: queryText,
        assigned_sme: assignSme,
        context: {
          standard: queryContext.standard,
          clause: queryContext.clause,
          report_id: queryContext.report_id,
          metric: queryContext.metric || undefined,
          value: queryContext.value ? parseFloat(queryContext.value) : undefined,
          analysis_id: queryContext.report_id || undefined,
        },
      };
      const res = await fetch(`${API}/auditiq/query`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const data = await res.json();
        setQueryLog(prev => [{
          log_id: data.log_id, timestamp: data.logged_at, query: queryText,
          response: data.response, storyboard: data.storyboard,
          status: data.status, outcome: "pending", assigned_sme: assignSme
        }, ...prev]);
        setQueryText(""); setAssignSme("");
      }
    } catch (e) { console.error(e); }
    setQuerying(false);
  };

  const updateRequestStatus = async (logId, newStatus, sme) => {
    try {
      await fetch(`${API}/auditiq/requests/${logId}/status`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus, assigned_sme: sme }),
      });
      setQueryLog(prev => prev.map(l => l.log_id === logId ? { ...l, status: newStatus } : l));
    } catch (e) { console.error(e); }
  };

  const relayToSme = async (logId, channel) => {
    setRelayingId(logId);
    try {
      const res = await fetch(`${API}/auditiq/requests/${logId}/relay`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ channel, sme_name: "SME" }),
      });
      if (res.ok) {
        const d = await res.json();
        setQueryLog(prev => prev.map(l => l.log_id === logId ? { ...l, relay_delivered: true, relay_channel: channel } : l));
      }
    } catch (e) { console.error(e); }
    setRelayingId(null);
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const updateOutcome = async (logId, outcome) => {
    try {
      await fetch(`${API}/auditiq/query-log/${logId}/outcome`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ outcome }),
      });
      setQueryLog(prev => prev.map(l => l.log_id === logId ? { ...l, outcome } : l));
    } catch (e) { console.error(e); }
  };

  return (
    <QMSLayout title={t('warRoom.title')}>
      <div data-testid="audit-war-room" className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
              <Shield className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white" style={{ fontFamily: "'Syne', sans-serif" }}>{t('warRoom.heading')}</h1>
              <p className="text-xs text-[#94A3B8]">{t('warRoom.subtitle')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {activeSession?.status === "active" ? (
              <>
                <div data-testid="audit-mode-indicator" className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/20 border border-red-500/40">
                  <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                  <span className="text-xs font-bold text-red-400 uppercase tracking-wider">{t('warRoom.auditMode')}</span>
                </div>
                <div className="px-3 py-1.5 rounded-lg bg-[#132340] border border-[#1E3A5F] font-mono text-sm text-[#00C8E0]">{formatTime(elapsedTime)}</div>
                <button data-testid="end-session-btn" onClick={endSession} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/20 border border-red-500/40 text-red-400 text-xs font-medium hover:bg-red-500/30 transition-colors">
                  <Square className="w-3.5 h-3.5" /> {t('warRoom.end')}
                </button>
              </>
            ) : (
              <button data-testid="start-audit-btn" onClick={() => setShowNewSession(true)} className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-500 text-white text-sm font-bold hover:bg-red-600 transition-colors">
                <Play className="w-4 h-4" /> {t('warRoom.startAuditMode')}
              </button>
            )}
          </div>
        </div>

        {/* New Session Modal */}
        {showNewSession && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-[#0F1E35] border border-[#1E3A5F] rounded-xl w-full max-w-lg p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-white">{t('warRoom.startSession')}</h2>
                <button onClick={() => setShowNewSession(false)} className="text-[#94A3B8] hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#94A3B8] mb-1 block">{t('warRoom.standard')}</label>
                  <select data-testid="session-standard-select" value={newSession.standard} onChange={e => setNewSession(p => ({ ...p, standard: e.target.value }))}
                    className="w-full px-3 py-2 bg-[#132340] border border-[#1E3A5F] rounded-lg text-sm text-white">{STANDARDS_LIST.map(s => <option key={s} value={s}>{s}</option>)}</select>
                </div>
                <div>
                  <label className="text-xs text-[#94A3B8] mb-1 block">Clause</label>
                  <input data-testid="session-clause-input" value={newSession.clause} onChange={e => setNewSession(p => ({ ...p, clause: e.target.value }))}
                    placeholder="e.g., 7.5.6" className="w-full px-3 py-2 bg-[#132340] border border-[#1E3A5F] rounded-lg text-sm text-white placeholder-[#94A3B8]/50" />
                </div>
                <div><label className="text-xs text-[#94A3B8] mb-1 block">{t('warRoom.auditorName')}</label>
                  <input data-testid="session-auditor-input" value={newSession.auditor_name} onChange={e => setNewSession(p => ({ ...p, auditor_name: e.target.value }))}
                    placeholder="Name" className="w-full px-3 py-2 bg-[#132340] border border-[#1E3A5F] rounded-lg text-sm text-white placeholder-[#94A3B8]/50" /></div>
                <div><label className="text-xs text-[#94A3B8] mb-1 block">{t('warRoom.auditorOrg')}</label>
                  <input value={newSession.auditor_org} onChange={e => setNewSession(p => ({ ...p, auditor_org: e.target.value }))}
                    placeholder="BSI / TUV" className="w-full px-3 py-2 bg-[#132340] border border-[#1E3A5F] rounded-lg text-sm text-white placeholder-[#94A3B8]/50" /></div>
                <div><label className="text-xs text-[#94A3B8] mb-1 block">Report ID</label>
                  <input value={newSession.report_id} onChange={e => setNewSession(p => ({ ...p, report_id: e.target.value }))}
                    placeholder="OQ-01-2026" className="w-full px-3 py-2 bg-[#132340] border border-[#1E3A5F] rounded-lg text-sm text-white placeholder-[#94A3B8]/50" /></div>
                <div><label className="text-xs text-[#94A3B8] mb-1 block">{t('warRoom.findingRisk')}</label>
                  <select value={newSession.finding_risk} onChange={e => setNewSession(p => ({ ...p, finding_risk: e.target.value }))}
                    className="w-full px-3 py-2 bg-[#132340] border border-[#1E3A5F] rounded-lg text-sm text-white">
                    <option value="LOW">LOW</option><option value="MEDIUM">MEDIUM</option><option value="HIGH">HIGH</option><option value="CRITICAL">CRITICAL</option>
                  </select></div>
              </div>
              <button data-testid="confirm-start-session-btn" onClick={startSession} disabled={loading}
                className="w-full py-2.5 rounded-lg bg-red-500 text-white font-bold text-sm hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                {loading ? t('warRoom.starting') : t('warRoom.activateAuditMode')}
              </button>
            </div>
          </div>
        )}

        {/* Main split */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4" style={{ minHeight: "calc(100vh - 220px)" }}>
          {/* Left: Scope + Context */}
          <div className="lg:col-span-4 space-y-3">
            <div className="bg-[#0F1E35] border border-[#1E3A5F] rounded-xl p-4 space-y-3">
              <h3 className="text-sm font-bold text-[#00C8E0] uppercase tracking-wider">{t('warRoom.auditScope')}</h3>
              {activeSession ? (
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-[#94A3B8]">{t('warRoom.session')}</span><span className="text-white font-mono text-xs">{activeSession.session_id}</span></div>
                  <div className="flex justify-between"><span className="text-[#94A3B8]">{t('warRoom.standard')}</span><span className="text-white">{activeSession.standard}</span></div>
                  {activeSession.clause && <div className="flex justify-between"><span className="text-[#94A3B8]">{t('warRoom.clause')}</span><span className="text-white">{activeSession.clause}</span></div>}
                  {activeSession.auditor_name && <div className="flex justify-between"><span className="text-[#94A3B8]">{t('warRoom.auditor')}</span><span className="text-white">{activeSession.auditor_name} ({activeSession.auditor_org})</span></div>}
                  <div className="flex justify-between"><span className="text-[#94A3B8]">{t('warRoom.risk')}</span>
                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${activeSession.finding_risk === "CRITICAL" ? "bg-red-500/20 text-red-400" : activeSession.finding_risk === "HIGH" ? "bg-orange-500/20 text-orange-400" : activeSession.finding_risk === "MEDIUM" ? "bg-amber-500/20 text-amber-400" : "bg-emerald-500/20 text-emerald-400"}`}>{activeSession.finding_risk}</span>
                  </div>
                </div>
              ) : <p className="text-[#94A3B8] text-sm">{t('warRoom.noActiveSession')}</p>}
            </div>

            {/* Request Status Filter */}
            <div className="bg-[#0F1E35] border border-[#1E3A5F] rounded-xl p-4 space-y-2">
              <h3 className="text-sm font-bold text-[#94A3B8] uppercase tracking-wider">{t('warRoom.requestStatusFilter')}</h3>
              <div className="flex flex-wrap gap-1">
                <button onClick={() => setStatusFilter("")} className={`px-2 py-1 rounded text-[10px] font-medium ${!statusFilter ? "bg-[#1A6BCC] text-white" : "bg-[#132340] text-[#94A3B8]"}`}>{t('warRoom.all')}</button>
                {Object.entries(STATUS_STYLES).map(([k, v]) => (
                  <button key={k} onClick={() => setStatusFilter(k)} className={`px-2 py-1 rounded text-[10px] font-medium ${statusFilter === k ? `${v.bg} ${v.text}` : "bg-[#132340] text-[#94A3B8]"}`}>{v.label}</button>
                ))}
              </div>
              {activeSession?.requests && (
                <div className="grid grid-cols-3 gap-1 mt-2">
                  {Object.entries(activeSession.requests).map(([k, v]) => (
                    <div key={k} className="text-center p-1.5 rounded bg-[#132340]">
                      <span className={`text-xs font-bold ${STATUS_STYLES[k]?.text || "text-[#94A3B8]"}`}>{v}</span>
                      <span className="text-[9px] text-[#94A3B8] block">{STATUS_STYLES[k]?.label || k}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Query Context */}
            <div className="bg-[#0F1E35] border border-[#1E3A5F] rounded-xl p-4 space-y-2">
              <h3 className="text-sm font-bold text-[#94A3B8] uppercase tracking-wider">{t('warRoom.queryContext')}</h3>
              <select value={queryContext.standard} onChange={e => setQueryContext(p => ({ ...p, standard: e.target.value }))}
                className="w-full px-2 py-1.5 bg-[#132340] border border-[#1E3A5F] rounded text-xs text-white">{STANDARDS_LIST.map(s => <option key={s} value={s}>{s}</option>)}</select>
              <input placeholder="Clause (e.g., 7.5.6)" value={queryContext.clause} onChange={e => setQueryContext(p => ({ ...p, clause: e.target.value }))}
                className="w-full px-2 py-1.5 bg-[#132340] border border-[#1E3A5F] rounded text-xs text-white placeholder-[#94A3B8]/50" />
              <input placeholder="Report / Analysis ID" value={queryContext.report_id} onChange={e => setQueryContext(p => ({ ...p, report_id: e.target.value }))}
                className="w-full px-2 py-1.5 bg-[#132340] border border-[#1E3A5F] rounded text-xs text-white placeholder-[#94A3B8]/50" />
              <div className="grid grid-cols-2 gap-2">
                <input placeholder="Metric" value={queryContext.metric} onChange={e => setQueryContext(p => ({ ...p, metric: e.target.value }))}
                  className="w-full px-2 py-1.5 bg-[#132340] border border-[#1E3A5F] rounded text-xs text-white placeholder-[#94A3B8]/50" />
                <input placeholder="Value" value={queryContext.value} onChange={e => setQueryContext(p => ({ ...p, value: e.target.value }))}
                  className="w-full px-2 py-1.5 bg-[#132340] border border-[#1E3A5F] rounded text-xs text-white placeholder-[#94A3B8]/50" />
              </div>
            </div>

            {/* Session History */}
            <div className="bg-[#0F1E35] border border-[#1E3A5F] rounded-xl p-4 space-y-2">
              <h3 className="text-sm font-bold text-[#94A3B8] uppercase tracking-wider">{t('warRoom.sessionHistory')}</h3>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {sessions.filter(s => s.session_id !== activeSession?.session_id).map(s => (
                  <button key={s.session_id} onClick={() => setActiveSession(s)}
                    className="w-full text-left flex items-center justify-between p-2 rounded-lg hover:bg-[#132340] text-xs">
                    <span className="text-white font-mono">{s.session_id}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${s.status === "active" ? "bg-red-500/20 text-red-400" : "bg-[#132340] text-[#94A3B8]"}`}>{s.status}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Query Engine + Request List */}
          <div className="lg:col-span-8 space-y-3">
            {/* Query input + SME assignment */}
            <div className="bg-[#0F1E35] border border-[#1E3A5F] rounded-xl p-4 space-y-3">
              <h3 className="text-sm font-bold text-[#00C8E0] uppercase tracking-wider">{t('warRoom.queryEngine')}</h3>
              <div className="flex gap-2">
                <input ref={queryInputRef} data-testid="query-input" value={queryText}
                  onChange={e => setQueryText(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submitQuery(); } }}
                  placeholder="Ask anything... e.g., 'justify sample size 30 for OQ-01 validation'"
                  className="flex-1 px-4 py-2.5 bg-[#132340] border border-[#1E3A5F] rounded-lg text-sm text-white placeholder-[#94A3B8]/50 focus:border-[#1A6BCC] focus:outline-none" />
                <button data-testid="send-query-btn" onClick={submitQuery} disabled={querying || !queryText.trim()}
                  className="px-4 py-2.5 rounded-lg bg-[#1A6BCC] text-white font-bold text-sm hover:bg-[#00C8E0] transition-colors disabled:opacity-50 flex items-center gap-2">
                  {querying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 flex-1">
                  <User className="w-3.5 h-3.5 text-[#94A3B8]" />
                  <input value={assignSme} onChange={e => setAssignSme(e.target.value)} placeholder={t('warRoom.assignSme')}
                    className="flex-1 px-2 py-1 bg-[#132340] border border-[#1E3A5F] rounded text-xs text-white placeholder-[#94A3B8]/50" />
                </div>
                <div className="flex flex-wrap gap-1">
                  {QUICK_QUERY_KEYS.map(q => (
                    <button key={q.labelKey} onClick={() => { setQueryText(q.prefix); queryInputRef.current?.focus(); }}
                      className="flex items-center gap-1 px-2 py-1 rounded-full bg-[#132340] border border-[#1E3A5F] text-[10px] text-[#94A3B8] hover:text-[#00C8E0] hover:border-[#00C8E0]/30 transition-colors">
                      <q.icon className="w-3 h-3" /> {t(q.labelKey)}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Request/Response list */}
            <div className="space-y-3 max-h-[calc(100vh-380px)] overflow-y-auto pr-1">
              {queryLog.length === 0 && (
                <div className="bg-[#0F1E35] border border-[#1E3A5F] rounded-xl p-8 text-center">
                  <Shield className="w-12 h-12 text-[#1E3A5F] mx-auto mb-3" />
                  <p className="text-[#94A3B8] text-sm">{t('warRoom.noRequests')}</p>
                  <p className="text-[#94A3B8]/60 text-xs mt-1">{t('warRoom.requestTracking')}</p>
                </div>
              )}

              {queryLog.map(log => {
                const st = STATUS_STYLES[log.status] || STATUS_STYLES.new;
                return (
                  <div key={log.log_id} data-testid="query-response-card" className="bg-[#0F1E35] border border-[#1E3A5F] rounded-xl overflow-hidden">
                    {/* Request header */}
                    <div className="px-4 py-2.5 bg-[#132340] flex items-center justify-between border-b border-[#1E3A5F]">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${st.bg} ${st.text} flex-shrink-0`}>{st.label}</span>
                        <span className="text-sm text-white font-medium truncate">{log.query}</span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {log.assigned_sme && <span className="text-[10px] text-purple-400"><User className="w-3 h-3 inline" /> {log.assigned_sme}</span>}
                        <span className="text-[10px] text-[#94A3B8] font-mono">{new Date(log.timestamp).toLocaleTimeString()}</span>
                        {log.response?.confidence_level && (
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${CONFIDENCE_STYLES[log.response.confidence_level] || CONFIDENCE_STYLES.LOW}`}>{log.response.confidence_level}</span>
                        )}
                      </div>
                    </div>
                    {/* Response */}
                    <div className="p-4 space-y-3">
                      {log.response?.plain_language && (
                        <div>
                          <div className="flex items-center gap-1.5 mb-1">
                            <span className="text-[10px] font-bold text-[#00C8E0] uppercase tracking-wider">{t('warRoom.plainEnglish')}</span>
                            <button onClick={() => copyToClipboard(log.response.plain_language, `${log.log_id}-pl`)} className="text-[#94A3B8] hover:text-white">
                              {copiedId === `${log.log_id}-pl` ? <CheckCircle2 className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                            </button>
                          </div>
                          <p className="text-sm text-[#94A3B8] leading-relaxed">{log.response.plain_language}</p>
                        </div>
                      )}
                      {log.response?.standard_clause && (
                        <div><span className="text-[10px] font-bold text-amber-400 uppercase tracking-wider">QMSR / Standard Clause</span>
                          <p className="text-sm text-white mt-1">{log.response.standard_clause}</p></div>
                      )}
                      {log.response?.math_basis && log.response.math_basis !== "N/A" && (
                        <div><span className="text-[10px] font-bold text-purple-400 uppercase tracking-wider">{t('warRoom.mathBasis')}</span>
                          <p className="text-sm text-[#94A3B8] mt-1 font-mono text-xs">{log.response.math_basis}</p></div>
                      )}
                      {log.response?.auditor_script && (
                        <div className="bg-[#132340] border border-[#1E3A5F] rounded-lg p-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-[10px] font-bold text-emerald-400 uppercase tracking-wider">Auditor Script (Relay to SME)</span>
                            <button data-testid="copy-script-btn" onClick={() => copyToClipboard(log.response.auditor_script, `${log.log_id}-as`)}
                              className="flex items-center gap-1 text-xs text-[#94A3B8] hover:text-[#00C8E0]">
                              {copiedId === `${log.log_id}-as` ? <><CheckCircle2 className="w-3 h-3 text-emerald-400" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                            </button>
                          </div>
                          <p className="text-sm text-white leading-relaxed">{log.response.auditor_script}</p>
                        </div>
                      )}
                      {/* Storyboard for Critical */}
                      {log.storyboard && log.storyboard.opening_statement && (
                        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 space-y-2">
                          <span className="text-[10px] font-bold text-red-400 uppercase tracking-wider">{t('warRoom.criticalStoryboard')}</span>
                          <p className="text-sm text-white font-medium">{log.storyboard.storyboard_title}</p>
                          <p className="text-sm text-[#94A3B8]">{log.storyboard.opening_statement}</p>
                          {log.storyboard.evidence_chain?.map((step, i) => (
                            <div key={i} className="flex items-start gap-2 text-xs">
                              <span className="w-5 h-5 rounded-full bg-red-500/20 text-red-400 flex items-center justify-center flex-shrink-0 text-[10px] font-bold">{step.step}</span>
                              <div><span className="text-white font-medium">{step.label}</span><p className="text-[#94A3B8]">{step.content}</p></div>
                            </div>
                          ))}
                          {log.storyboard.closing_statement && <p className="text-sm text-white italic">{log.storyboard.closing_statement}</p>}
                        </div>
                      )}
                    </div>
                    {/* Actions footer */}
                    <div className="px-4 py-2 bg-[#132340]/50 flex items-center justify-between border-t border-[#1E3A5F] gap-2 flex-wrap">
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] text-[#94A3B8]">{t('warRoom.status')}</span>
                        {["sme_ready", "escalation", "critical", "resolved"].map(s => (
                          <button key={s} onClick={() => updateRequestStatus(log.log_id, s)}
                            className={`px-1.5 py-0.5 rounded text-[10px] font-medium transition-colors ${log.status === s ? `${STATUS_STYLES[s].bg} ${STATUS_STYLES[s].text}` : "text-[#94A3B8]/50 hover:text-[#94A3B8]"}`}>
                            {STATUS_STYLES[s].label}
                          </button>
                        ))}
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] text-[#94A3B8]">{t('warRoom.relay')}</span>
                        {["slack", "teams", "whatsapp"].map(ch => (
                          <button key={ch} onClick={() => relayToSme(log.log_id, ch)} disabled={relayingId === log.log_id}
                            data-testid={`relay-${ch}-btn`}
                            className={`px-1.5 py-0.5 rounded text-[10px] font-medium transition-colors ${log.relay_channel === ch && log.relay_delivered ? "bg-emerald-500/20 text-emerald-400" : "bg-[#132340] text-[#94A3B8] hover:text-white"}`}>
                            {ch === "slack" ? <Hash className="w-3 h-3 inline" /> : ch === "teams" ? <MessageSquare className="w-3 h-3 inline" /> : <Phone className="w-3 h-3 inline" />}
                            {" "}{ch}
                          </button>
                        ))}
                      </div>
                      <div className="flex items-center gap-1">
                        {["finding_averted", "finding_issued"].map(o => (
                          <button key={o} onClick={() => updateOutcome(log.log_id, o)}
                            className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${log.outcome === o ? (o === "finding_averted" ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400") : "text-[#94A3B8]/50 hover:text-[#94A3B8]"}`}>
                            {o.replace("_", " ")}
                          </button>
                        ))}
                        <span className="text-[10px] text-[#94A3B8] font-mono ml-1">{log.log_id}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </QMSLayout>
  );
};

export default AuditWarRoomPage;
